// ANGULAR
import { Injectable } from '@angular/core';

// NGRX
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

// RXJS
import { Observable, of } from 'rxjs';
import { catchError, mergeMap, withLatestFrom } from 'rxjs/operators';

// SERVICES
import { MasterService } from '../services/master.service';

// ACTIONS AND STATES
import * as globalActions from '../../global/store/actions';
import { State } from '../../root-state';
import * as masterActions from './actions';

// COMMON INTERFACES AND ENUMS
import { ModalType } from '../../../models/common/modal/modal-config';

// CONSTANT
import { API_METHOD_CONSTANTS } from '../../../../app/constants/ngrx-store/core';
import { MASTER_MODULE_CONSTANTS, MASTER_NGRX_CONSTANTS } from '../../../constants/ngrx-store/master';

/**
 * This effect is for listen master actions, execute master service call and dispatch in master reducer.
 * For master api call purpose.
 *
 * @author Pratik Shihora <pratik@saeculumsolutions.com>
 *
 * Notes:-
 * Date: 16/03/2020 (Pratik Shihora <pratik@saeculumsolutions.com>) master effect create
 */
@Injectable()
export class MasterEffects {
  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private masterService: MasterService) { }

  /**
	  * Master Effect
	  * @param type : GET_MASTER action name.
	  * @param payload : object to identify common master method base on module and method.
	  * @returns Empty action dispatch to reducer.
	  */
  getMasterEffect$ = createEffect(() => this.actions$.pipe(
    ofType<masterActions.GetMasterAction>(masterActions.ActionTypes.GET_MASTER),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {


      // get common constant base action module and method.
      // changed to payload type from payload.data.method
      const common = MASTER_NGRX_CONSTANTS[action.payload.module][action.payload.method];

      // avoid service call if data already exists in store.
      // avoid service call if data already exists in store.
      if (common.CACHING && !common.INDIVIDUAL && state.business && state.business[common.STATE]) {
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.business[common.STATE] });
        }
        // make empty action call for skip
        return of(new globalActions.EmptyAction({}));
      } else if (common.CACHING && common.INDIVIDUAL && state.business &&
        state.business[common.STATE] &&
        state.business[common.STATE][action.payload.data[common.UNIQUE_KEY]]) {

        // data send from redux store if already available
        if (action.payload.callback) {
          action.payload.callback({ success: true, data: state.business[common.STATE][action.payload.data[common.UNIQUE_KEY]] });
        }

        return of(new globalActions.EmptyAction({}));
      }

      return this.masterService[common.API_METHOD](action.payload.data)
        .pipe(
          mergeMap((data) => {
            let actions: any[] = [];

            // dispatch master success action
            actions = [new masterActions.GetMasterSuccessAction({ data, common })];

            // dispatch popup notification for open success popup
            if (common.SUCCESS_NOTIFICATION) {
              actions.push(new globalActions.PopupNotificationAction({
                actionType: masterActions.ActionTypes.GET_MASTER, type: ModalType.SUCCESS,
                message: common.MESSAGE, callback: action.payload.callback, title: common.TITLE,
              }));
            } else {
              action.payload.callback && action.payload.callback({ success: true, data });
            }

            return actions;
          }),
          catchError(error =>
            // dispatch popup notification for open error popup
            of(new globalActions.PopupNotificationAction({
              actionType: masterActions.ActionTypes.GET_MASTER, type: ModalType.ERROR,
              message: error.error.message, callback: action.payload.callback, error: error.error,
              title: common.TITLE,
            }))));
    }),
  ));


  /**
	  * State By Country Effect
	  * @param type : GET_STATE_BY_COUNTRY Action
	  */
  getStateByCountryEffect$ = createEffect(() => this.actions$.pipe(
    ofType<masterActions.GetMasterAction>(masterActions.ActionTypes.GET_STATE_BY_COUNTRY),
    withLatestFrom(this.store),
    mergeMap(([action, state]): Observable<any> => {
      // get common constant base action module and method.
      const common = MASTER_NGRX_CONSTANTS[MASTER_MODULE_CONSTANTS.STATE][API_METHOD_CONSTANTS.GET_BY_ID];

      // avoid service call if data already exists in store.
      if (state.master && state.master.stateLookUp && state.master.stateLookUp[action.payload.data.country_id]) {
        return of(new globalActions.EmptyAction({}));
      }

      return this.masterService.getStateList(action.payload)
        .pipe(
          mergeMap((data: any) => {
            let actions: any[] = [];
            // dispatch master success action
            actions = [new masterActions.GetStateByCountrySuccessAction({ action: action.payload.data, data, common })];

            return actions;
          }),
          catchError(error => {
            // dispatch popup notification for open error popup
            return of(new globalActions.PopupNotificationAction({
              actionType: masterActions.ActionTypes.GET_MASTER, type: ModalType.ERROR,
              message: error.error.message, callback: action.payload.callback, error: error.error,
              title: common.TITLE,
            }));
          },
          ));
    }),
  ));
}
